import { SetupIntent } from '@stripe/stripe-js';
import { IPaymentCard } from '../../types/global/generic.types';
import type { IWalletPaymentRail } from '../buzzbridge/types';

export const types = {
    ADD_PAYMENT_METHOD: 'ADD_PAYMENT_METHOD',
    ADD_PAYMENT_METHOD_SUCCESS: 'ADD_PAYMENT_METHOD_SUCCESS',
    ADD_PAYMENT_METHOD_FAIL: 'ADD_PAYMENT_METHOD_FAIL',

    UPDATE_PAYMENT_METHOD: 'UPDATE_PAYMENT_METHOD',
    UPDATE_PAYMENT_METHOD_SUCCESS: 'UPDATE_PAYMENT_METHOD_SUCCESS',
    UPDATE_PAYMENT_METHOD_FAIL: 'UPDATE_PAYMENT_METHOD_FAIL',

    DELETE_PAYMENT_METHOD: 'DELETE_PAYMENT_METHOD',
    DELETE_PAYMENT_METHOD_SUCCESS: 'DELETE_PAYMENT_METHOD_SUCCESS',
    DELETE_PAYMENT_METHOD_FAIL: 'DELETE_PAYMENT_METHOD_FAIL',

    GET_PAYMENT_METHODS: 'GET_PAYMENT_METHODS',
    GET_PAYMENT_METHODS_SUCCESS: 'GET_PAYMENT_METHODS_SUCCESS',
    GET_PAYMENT_METHODS_FAIL: 'GET_PAYMENT_METHODS_FAIL',

    CREATE_PAYMENT_INTENT: 'CREATE_PAYMENT_INTENT',
    CREATE_PAYMENT_INTENT_SUCCESS: 'CREATE_PAYMENT_INTENT_SUCCESS',
    CREATE_PAYMENT_INTENT_FAIL: 'CREATE_PAYMENT_INTENT_FAIL',

    CREATE_PAYMENT_INTENT_FOR_PURCHASE: 'CREATE_PAYMENT_INTENT_FOR_PURCHASE',
    CREATE_PAYMENT_INTENT_FOR_PURCHASE_SUCCESS: 'CREATE_PAYMENT_INTENT_FOR_PURCHASE_SUCCESS',
    CREATE_PAYMENT_INTENT_FOR_PURCHASE_FAIL: 'CREATE_PAYMENT_INTENT_FOR_PURCHASE_FAIL',

    GET_PUBLISHABLE_KEY: 'GET_PUBLISHABLE_KEY',
    GET_PUBLISHABLE_KEY_SUCCESS: 'GET_PUBLISHABLE_KEY_SUCCESS',
    GET_PUBLISHABLE_KEY_FAIL: 'GET_PUBLISHABLE_KEY_FAIL',

    GET_PAYMENT_RAIL: 'GET_PAYMENT_RAIL',
    GET_PAYMENT_RAIL_SUCCESS: 'GET_PAYMENT_RAIL_SUCCESS',
    GET_PAYMENT_RAIL_FAIL: 'GET_PAYMENT_RAIL_FAIL',

    FETCH_ADMIN_PAYMENT_RAIL_COUNTRIES: 'FETCH_ADMIN_PAYMENT_RAIL_COUNTRIES',
    FETCH_ADMIN_PAYMENT_RAIL_COUNTRIES_SUCCESS: 'FETCH_ADMIN_PAYMENT_RAIL_COUNTRIES_SUCCESS',
    FETCH_ADMIN_PAYMENT_RAIL_COUNTRIES_FAIL: 'FETCH_ADMIN_PAYMENT_RAIL_COUNTRIES_FAIL',

    SAVE_ADMIN_PAYMENT_RAIL_COUNTRIES: 'SAVE_ADMIN_PAYMENT_RAIL_COUNTRIES',
    SAVE_ADMIN_PAYMENT_RAIL_COUNTRIES_SUCCESS: 'SAVE_ADMIN_PAYMENT_RAIL_COUNTRIES_SUCCESS',
    SAVE_ADMIN_PAYMENT_RAIL_COUNTRIES_FAIL: 'SAVE_ADMIN_PAYMENT_RAIL_COUNTRIES_FAIL',

    ASSIGN_ADMIN_PAYMENT_RAIL_COUNTRIES: 'ASSIGN_ADMIN_PAYMENT_RAIL_COUNTRIES',
    ASSIGN_ADMIN_PAYMENT_RAIL_COUNTRIES_SUCCESS: 'ASSIGN_ADMIN_PAYMENT_RAIL_COUNTRIES_SUCCESS',
    ASSIGN_ADMIN_PAYMENT_RAIL_COUNTRIES_FAIL: 'ASSIGN_ADMIN_PAYMENT_RAIL_COUNTRIES_FAIL',

    VERIFY_PAYSTACK_PURCHASE: 'VERIFY_PAYSTACK_PURCHASE',
    VERIFY_PAYSTACK_PURCHASE_SUCCESS: 'VERIFY_PAYSTACK_PURCHASE_SUCCESS',
    VERIFY_PAYSTACK_PURCHASE_FAIL: 'VERIFY_PAYSTACK_PURCHASE_FAIL',

    VERIFY_FLUTTERWAVE_PURCHASE: 'VERIFY_FLUTTERWAVE_PURCHASE',
    VERIFY_FLUTTERWAVE_PURCHASE_SUCCESS: 'VERIFY_FLUTTERWAVE_PURCHASE_SUCCESS',
    VERIFY_FLUTTERWAVE_PURCHASE_FAIL: 'VERIFY_FLUTTERWAVE_PURCHASE_FAIL',

    INITIALIZE_FLUTTERWAVE_BILLING_CARD: 'INITIALIZE_FLUTTERWAVE_BILLING_CARD',
    VERIFY_FLUTTERWAVE_BILLING_CARD: 'VERIFY_FLUTTERWAVE_BILLING_CARD',
};

export interface IPaymentInitialState {
    paymentMethods?: IPaymentCard[];
    loading?: boolean;
    errMsg?: any;
    paymentIntentToken?: string;
    /** Active rail publishable key (Paystack / Flutterwave / Stripe). */
    publishableKey?: string;
    /** Provider that last set `publishableKey` for the active billing rail. */
    publishableKeySource?: 'stripe' | 'paystack' | 'flutterwave';
    /** Cached keys so Stripe background fetches do not overwrite Paystack. */
    publishableKeys?: {
        stripe?: string;
        paystack?: string;
        flutterwave?: string;
    };
    isNonUs?: boolean;
    error?: any;
}

export type PaymentIntentStatus =
    | 'requires_payment_method'
    | 'requires_confirmation'
    | 'requires_action'
    | 'processing'
    | 'requires_capture'
    | 'canceled'
    | 'succeeded';

export interface PaymentIntentDTO {
    payment_method_types?: string[]; // e.g. ['card', 'wallet]
    latest_charge?: string | null; // charge ID
    capture_method?: 'automatic' | 'manual';
    confirmation_method?: 'automatic' | 'manual';
    next_action?: {
        type:
            | 'use_stripe_sdk'
            | 'redirect_to_url'
            | 'verify_with_microdeposits'
            | 'display_bank_transfer_instructions'
            | string;
        redirect_to_url?: {
            url: string;
            return_url?: string | null;
        } | null;
        use_stripe_sdk?: unknown;
        // add more fields you actually consume
    } | null;
    automatic_payment_methods?: {
        enabled?: boolean;
    };
}

export interface ICreatePaymentIntent {
    /** card */
    payment_method_type?: string;
    /**usd aud */
    currency?: string;
    /** amount in smallest unit */
    amount?: number;
    save_card?: boolean;
    metadata?: string | Record<string, unknown>;
    stripe_payload?: PaymentIntentDTO;
    provider?: 'paystack' | 'stripe' | 'flutterwave';
    callback_url?: string;
}

export interface IPaymentIntentResponse {
    clientSecret: string;
    provider?: 'stripe';
    paymentIntentId?: string;
    saleId?: string;
    stripe_region?: 'us' | 'eu' | null;
    stripe_publishable_key?: string | null;
    payment_rail?: IWalletPaymentRail;
}

export interface IPaymentRailQuery {
    category?: string;
    provider?: 'stripe' | 'paystack' | 'flutterwave';
    source?: 'app_clip' | 'clip';
    payment_method_type?: string;
}

/** Flat rail payload from GET /payment/rail (may also nest under `payment_rail`). */
export interface IPaymentRailResponse extends IWalletPaymentRail {
    payment_rail?: IWalletPaymentRail;
    country_name?: string;
}

export interface IPaystackPurchaseResponse {
    provider: 'paystack' | 'flutterwave';
    reference?: string;
    tx_ref?: string;
    authorization_url: string;
    access_code?: string;
    saleId?: string;
    amount?: number;
    currency?: string;
    payment_rail?: IWalletPaymentRail;
}

export type IPurchaseCheckoutResponse = IPaymentIntentResponse | IPaystackPurchaseResponse;

export interface IVerifyPaystackPurchasePayload {
    reference: string;
}

export interface IVerifyPaystackPurchaseResult {
    paid?: boolean;
    category?: string;
    sale_completed?: boolean;
    verified?: boolean;
    reference?: string;
    paystack_status?: string;
}

export interface IVerifyFlutterwavePurchasePayload {
    transaction_id?: string;
    tx_ref?: string;
    reference?: string;
}

export interface IVerifyFlutterwavePurchaseResult {
    paid?: boolean;
    category?: string;
    sale_completed?: boolean;
    verified?: boolean;
    reference?: string;
    tx_ref?: string;
    flutterwave_status?: string;
}

export interface IGetStripePublicKey {}

export interface IAddNewCard {
    /** Stripe SetupIntent / PaymentMethod id path */
    payment_method?: string;
    fullName?: string;
    intent?: SetupIntent | string;
    /** Paystack popup success — route to /payment/add-payment-method */
    isNonUs?: boolean;
    reference?: string;
    trxref?: string;
    status?: string;
    message?: string;
    trans?: string;
    transaction?: string;
}

export interface IGetPublishableKey {
    source: 'stripe' | 'paystack' | 'flutterwave';
}

export interface IGetPublishableKeyResponse {
    publishableKey: string;
    /** Returns whether the current user is within AFrica, to use Psyatck */
    isNonUs: boolean;
    provider?: 'stripe' | 'paystack' | 'flutterwave';
    stripe_region?: 'us' | 'eu' | null;
    payment_rail?: IWalletPaymentRail;
}

export interface IInitializeFlutterwaveBillingCardPayload {
    callback_url?: string;
    currency?: string;
}

export interface IVerifyFlutterwaveBillingCardPayload {
    transaction_id?: string;
    tx_ref?: string;
    reference?: string;
}
